import React, { useState, useEffect, useContext } from "react";
import { format } from "date-fns";
import { db } from "../../firebase";
import { onSnapshot, doc } from "firebase/firestore";
import { IonAvatar, IonIcon, IonSpinner } from "@ionic/react";
import { calendarOutline, locationOutline, openOutline } from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { ChatPanel } from "./ChatPanel";
import { WERK_SEGMENT_SELECTOR, WORKFLOW_SCHEDULER_TYPES, WORKFLOW_WERKER_TYPES, FB_COLLECTION_PATH } from "../../functions/postActionTypes";
import { ProfileContext } from "../../context/ProfileContext";

interface JobChatPanelProps {
  jobId: string | null;
  chatId: string | null;
  isScheduler: boolean;
}

interface MiniJobData {
  SchedFirstName: string;
  SchedLastName: string;
  SchedProfilePic: string;
  JobIdentifier: string;
  StartDate: string;
  Location: string;
  SchedJobStatus: string;
  WerkerJobStatus: string;
  SchedulerUid: string;
}

export const JobChatPanel: React.FC<JobChatPanelProps> = ({ jobId, chatId, isScheduler: _isScheduler }) => {
  const { profile } = useContext(ProfileContext);
  const [jobData, setJobData] = useState<MiniJobData | null>(null);
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!jobId) {
      setJobData(null);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(doc(db, FB_COLLECTION_PATH.JobsDocs, jobId), (snap) => {
      setLoading(false);
      if (snap.exists()) {
        const d = snap.data();
        setJobData({
          SchedFirstName: d.docStamp?.userInfoFrom?.firstName || "",
          SchedLastName: d.docStamp?.userInfoFrom?.lastName || "",
          SchedProfilePic: d.docStamp?.userInfoFrom?.profilePicURL || "",
          JobIdentifier: d.jobInfo?.core?.shiftIdentifier || "",
          StartDate: d.jobInfo?.core?.dateTimeStart || "",
          Location: d.jobInfo?.core?.location || "",
          SchedJobStatus: d.jobInfo?.status?.jobStatusSched || "",
          WerkerJobStatus: d.jobInfo?.status?.jobStatusWerker || "",
          SchedulerUid: d.docStamp?.userInfoFrom?.uid || "",
        });
      } else {
        setJobData(null);
      }
    });
    return () => unsub();
  }, [jobId]);

  const handleHeaderClick = () => {
    if (!jobId || !jobData) return;
    let segment: string;
    
    // Determine if current user is the scheduler based on UID comparison
    const isUserScheduler = jobData.SchedulerUid === profile.UserId;
    
    if (isUserScheduler) {
      if (jobData.SchedJobStatus === WORKFLOW_SCHEDULER_TYPES.Filled) segment = WERK_SEGMENT_SELECTOR.SchedFilled;
      else if (jobData.SchedJobStatus === WORKFLOW_SCHEDULER_TYPES.Past) segment = WERK_SEGMENT_SELECTOR.SchedPast;
      else segment = WERK_SEGMENT_SELECTOR.SchedOpen;
    } else {
      if (jobData.WerkerJobStatus === WORKFLOW_WERKER_TYPES.Scheduled) segment = WERK_SEGMENT_SELECTOR.WerkerScheduled;
      else if (jobData.WerkerJobStatus === WORKFLOW_WERKER_TYPES.Past) segment = WERK_SEGMENT_SELECTOR.WerkerPast;
      else segment = WERK_SEGMENT_SELECTOR.WerkerInvited;
    }
    history.push("/Home", { segment, jobId });
  };

  const formattedDate = (() => {
    if (!jobData?.StartDate) return "";
    try {
      return format(new Date(jobData.StartDate.substring(0, 19)), "MMM d, yyyy · h:mma");
    } catch {
      return jobData.StartDate;
    }
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Job header – top 30% */}
      <div
        onClick={handleHeaderClick}
        title="View job details"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 16px",
          background: "var(--ion-color-light)",
          borderBottom: "2px solid var(--ion-color-light-shade)",
          overflow: "hidden",
          flexShrink: 0,
          cursor: jobData ? "pointer" : "default",
        }}
      >
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <IonSpinner />
          </div>
        ) : jobData ? (
            <div style={{ display: "flex", alignItems: "center", gap: 14, width: "100%" }}>
            <IonAvatar style={{ width: 44, height: 44, flexShrink: 0 }}>
              <img
                src={jobData.SchedProfilePic || "../assets/profilePic.png"}
                alt="scheduler"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "../assets/profilePic.png";
                }}
              />
            </IonAvatar>
            <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: "var(--ion-color-medium)",
                  marginBottom: 1,
                }}
              >
                {jobData.SchedFirstName} {jobData.SchedLastName}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "var(--ion-text-color)",
                  marginBottom: 4,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {jobData.JobIdentifier || "Job"}
                <IonIcon icon={openOutline} style={{ fontSize: 12, flexShrink: 0, opacity: 0.5 }} />
              </div>
              {formattedDate && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: "0.78rem",
                    color: "var(--ion-color-medium)",
                    marginBottom: 2,
                  }}
                >
                  <IonIcon icon={calendarOutline} style={{ fontSize: 13, flexShrink: 0 }} />
                  <span>{formattedDate}</span>
                </div>
              )}
              {jobData.Location && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: "0.78rem",
                    color: "var(--ion-color-medium)",
                  }}
                >
                  <IonIcon icon={locationOutline} style={{ fontSize: 13, flexShrink: 0 }} />
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {jobData.Location}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Chat panel – bottom 70% */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <ChatPanel chatId={chatId} hideHeader />
      </div>
    </div>
  );
};
